"use client";

import React, { useMemo } from "react";
import { LinkAnchor } from "./link-anchor";
import { ChevronRight } from "lucide-react";
import { usePublicPathname } from "@/app/hooks/use-public-pathname";
import { slugToLabel } from "@/app/utils/helpers/backend/text-to-slug";
import { useBrand } from "@/app/components/theme/brand-theme-boundary";
import { cn } from "@/lib/utils";
import Container from "./container";

export type BreadcrumbItem = {
  label: string;
  href?: string;
};

export interface BreadcrumbProps {
  /**
   * Optional: Überschreibt die automatisch generierten Breadcrumbs.
   */
  items?: BreadcrumbItem[];
  className?: string;
  listClassName?: string;
  /**
   * Optional: Startpunkt, z.B. "Startseite". Standard: Startseite
   */
  rootLabel?: string;
  /**
   * Optional: Mapping von Segmenten zu Labels (z.B. { "oeffnungszeiten": "Öffnungszeiten" })
   */
  labelMap?: Record<string, string>;
  /**
   * Optional: Hintergrundfarbe für intelligente Hover-Effekte.
   * Standard: "default" (weißer/normaler Hintergrund)
   */
  background?: "default" | "muted" | "accent";
  /**
   * Optional: Erzwingt eine lokale Darstellungsvariante statt des Brand-Bars.
   */
  appearance?: "auto" | "plain-dark";
}

/**
 * Breadcrumb-Komponente für die Navigation.
 * Standardmäßig werden die Breadcrumbs aus der aktuellen URL generiert.
 * Links werden mit LinkAnchor gerendert, das aktuelle Element als Link in primary.
 */
export const Breadcrumb: React.FC<BreadcrumbProps> = ({
  items,
  className,
  listClassName,
  rootLabel = "Startseite",
  labelMap = {},
  background = "default",
  appearance = "auto",
}) => {
  const pathname = usePublicPathname();
  const { isSkoda, isAudi } = useBrand();
  const usesBrandBar = appearance === "auto" && (isSkoda || isAudi);

  // Intelligente Hover-Farbe basierend auf Hintergrund
  const getHoverClass = (isLast: boolean) => {
    if (appearance === "plain-dark") {
      return "text-black font-normal hover:text-black/70 focus-visible:ring-2 focus-visible:ring-black";
    }

    if (isSkoda && appearance === "auto") {
      return "text-white font-normal hover:text-black focus-visible:ring-2 focus-visible:ring-white";
    }

    if (isAudi && appearance === "auto") {
      return "text-white font-normal hover:text-white/70 focus-visible:ring-2 focus-visible:ring-white";
    }

    const baseClasses = isLast
      ? "text-primary font-normal focus-visible:ring-2 focus-visible:ring-primary"
      : "";

    // Hover-Klassen je nach Hintergrund
    const hoverMap = {
      default: "hover:bg-accent", // Auf weißem Hintergrund → hellgrauer Hover
      muted: "hover:bg-background", // Auf grauem Hintergrund → weißer Hover
      accent: "hover:bg-muted", // Auf accent Hintergrund → grauer Hover
    };

    const hoverClass = hoverMap[background] || hoverMap.default;
    return `${baseClasses} ${hoverClass}`.trim();
  };

  // Dynamische Breadcrumbs aus der URL generieren, falls keine items übergeben werden
  const dynamicItems = useMemo<BreadcrumbItem[]>(() => {
    if (items && items.length > 0) return items;
    if (!pathname) return [];
    const segments = pathname.split("/").filter(Boolean);
    const crumbs: BreadcrumbItem[] = [
      { label: rootLabel, href: "/" },
      ...segments.map((seg, idx) => {
        // Label aus Mapping oder automatisch via slugToLabel generieren
        const label = labelMap[seg] || slugToLabel(seg);
        // Bis zum aktuellen Segment aufbauen (URL bleibt mit ae/oe/ue)
        const href = "/" + segments.slice(0, idx + 1).join("/");
        return { label, href };
      }),
    ];
    return crumbs;
  }, [items, pathname, rootLabel, labelMap]);

  if (!dynamicItems || dynamicItems.length === 0) return null;

  const breadcrumbList = (
    <ol
      className={cn(
        "flex min-w-0 flex-wrap items-center gap-0 py-6 text-sm font-normal",
        appearance === "plain-dark"
          ? "text-black"
          : usesBrandBar
            ? "text-white"
            : "text-muted-foreground",
        listClassName
      )}
    >
      {dynamicItems.map((item, idx) => {
        const isLast = idx === dynamicItems.length - 1;
        return (
          <li
            key={item.label + idx}
            className={cn(
              "flex min-w-0 items-center font-light",
              isLast && "max-w-full flex-1 basis-auto"
            )}
          >
            <LinkAnchor
              href={item.href ?? "#"}
              underline="none"
              size="md"
              variant="default"
              className={cn(
                getHoverClass(isLast),
                isLast && "max-w-full min-w-0 break-words whitespace-normal"
              )}
              aria-current={isLast ? "page" : undefined}
            >
              {item.label}
            </LinkAnchor>
            {!isLast && (
              <span className="flex items-center px-1">
                <ChevronRight
                  className={cn(
                    "h-4 w-4",
                    appearance === "plain-dark"
                      ? "text-black"
                      : usesBrandBar
                        ? "text-white"
                        : "text-muted-foreground"
                  )}
                  aria-hidden
                />
              </span>
            )}
          </li>
        );
      })}
    </ol>
  );

  if (isSkoda && appearance === "auto") {
    return (
      <nav className="bg-[#0E3A2F]" aria-label="Breadcrumb">
        <Container className={className}>{breadcrumbList}</Container>
      </nav>
    );
  }

  if (isAudi && appearance === "auto") {
    return (
      <nav className="bg-[#10131A]" aria-label="Breadcrumb">
        <Container className={className}>{breadcrumbList}</Container>
      </nav>
    );
  }

  return (
    <nav className={cn(className)} aria-label="Breadcrumb">
      {breadcrumbList}
    </nav>
  );
};
